import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  Contact,
  CreateOpportunityInput,
  Customer,
  Opportunity,
  OpportunityKind,
  OpportunityStage,
  OpportunityStatus,
} from '@catalyst-psa/shared';
import {
  OPPORTUNITY_KINDS,
  OPPORTUNITY_KIND_LABELS,
  OPPORTUNITY_STAGES,
  OPPORTUNITY_STATUSES,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal, type ModalTab } from '../../components/ui/Modal.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { useContacts } from '../contacts/api.js';
import { useCustomers } from '../customers/api.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useCpqStatus } from '../cpq/api.js';
import { useMemberOptions } from '../members/api.js';
import { OnboardingRecordsTab } from '../onboarding/OnboardingRecordsTab.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { useCreateOpportunity, useDeleteOpportunity, useUpdateOpportunity } from './api.js';
import { OpportunityAttachmentsSection } from './OpportunityAttachmentsSection.js';
import { OpportunityCpqQuoteSection } from './OpportunityCpqQuoteSection.js';
import { OpportunityNotesSection } from './OpportunityNotesSection.js';
import { OpportunityProductsSection } from './OpportunityProductsSection.js';
import { OpportunityQuotesSection } from '../quotes/OpportunityQuotesSection.js';
import { QuickCreateContactModal, QuickCreateCustomerModal } from './QuickCreateModals.js';
import { OPPORTUNITY_STAGE_LABELS, OPPORTUNITY_STATUS_LABELS } from './status.js';

interface OpportunityFormModalProps {
  isOpen: boolean;
  /** Existing opportunity to edit, or null to create a new one. */
  opportunity: Opportunity | null;
  /** Pre-selected company id when creating (e.g. launched from a customer tab). */
  initialCustomerId?: string;
  onClose: () => void;
}

interface FormState {
  customerId: string;
  name: string;
  contactId: string;
  salesRepId: string;
  serviceCategoryId: string;
  closeDate: string;
  stage: OpportunityStage;
  status: OpportunityStatus;
  kind: '' | OpportunityKind;
  netSuiteTeamContactIds: string[];
  lastReviewedDate: string;
  summary: string;
  currentProductCategoryIds: string[];
  currentServiceCategoryIds: string[];
  nsReg: string;
  avalaraReg: string;
  lengthOfProject: string;
  lengthOfAgreement: string;
  marketingAcquisition: boolean;
}

const EMPTY_FORM: FormState = {
  customerId: '',
  name: '',
  contactId: '',
  salesRepId: '',
  serviceCategoryId: '',
  closeDate: '',
  stage: 'lead',
  status: 'open',
  kind: '',
  netSuiteTeamContactIds: [],
  lastReviewedDate: '',
  summary: '',
  currentProductCategoryIds: [],
  currentServiceCategoryIds: [],
  nsReg: '',
  avalaraReg: '',
  lengthOfProject: '',
  lengthOfAgreement: '',
  marketingAcquisition: false,
};

function toFormState(opportunity: Opportunity | null): FormState {
  if (!opportunity) {
    return EMPTY_FORM;
  }
  return {
    customerId: opportunity.customerId,
    name: opportunity.name,
    contactId: opportunity.contactId ?? '',
    salesRepId: opportunity.salesRepId ?? '',
    serviceCategoryId: opportunity.serviceCategoryId ?? '',
    closeDate: opportunity.closeDate ?? '',
    stage: opportunity.stage,
    status: opportunity.status,
    kind: opportunity.kind ?? '',
    netSuiteTeamContactIds: opportunity.netSuiteTeamContactIds,
    lastReviewedDate: opportunity.lastReviewedDate ?? '',
    summary: opportunity.summary ?? '',
    currentProductCategoryIds: opportunity.currentProductCategoryIds,
    currentServiceCategoryIds: opportunity.currentServiceCategoryIds,
    nsReg: opportunity.nsReg ?? '',
    avalaraReg: opportunity.avalaraReg ?? '',
    lengthOfProject: opportunity.lengthOfProject == null ? '' : String(opportunity.lengthOfProject),
    lengthOfAgreement:
      opportunity.lengthOfAgreement == null ? '' : String(opportunity.lengthOfAgreement),
    marketingAcquisition: opportunity.marketingAcquisition,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

const STAGE_OPTIONS = OPPORTUNITY_STAGES.map((stage) => ({
  value: stage,
  label: OPPORTUNITY_STAGE_LABELS[stage],
}));

const STATUS_OPTIONS = OPPORTUNITY_STATUSES.map((status) => ({
  value: status,
  label: OPPORTUNITY_STATUS_LABELS[status],
}));

const KIND_OPTIONS = [
  { value: '', label: 'Not set' },
  ...OPPORTUNITY_KINDS.map((kind) => ({ value: kind, label: OPPORTUNITY_KIND_LABELS[kind] })),
];

/** A labeled grid of checkboxes for selecting a set of ids (service categories, contacts). */
function MultiCheckGrid({
  label,
  options,
  selected,
  emptyText,
  onToggle,
}: {
  label: string;
  options: ReadonlyArray<{ id: string; name: string }>;
  selected: string[];
  emptyText: string;
  onToggle: (id: string, checked: boolean) => void;
}): JSX.Element {
  return (
    <div>
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border border-slate-200 p-3">
        {options.length === 0 ? (
          <p className="col-span-2 text-xs text-slate-400">{emptyText}</p>
        ) : (
          options.map((option) => (
            <CheckboxField
              key={option.id}
              label={option.name}
              checked={selected.includes(option.id)}
              onChange={(e) => onToggle(option.id, e.target.checked)}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * A searchable multi-select: shows the chosen items as removable chips and a filter
 * box that opens a dropdown of matching, unselected options. Used where the option list
 * is too long for a checkbox grid (e.g. the NetSuite Team contacts).
 */
function MultiSelectSearch({
  label,
  options,
  selected,
  emptyText,
  placeholder,
  onToggle,
  serverSearch = false,
  onQueryChange,
  nameById,
}: {
  label: string;
  options: ReadonlyArray<{ id: string; name: string }>;
  selected: string[];
  emptyText: string;
  placeholder: string;
  onToggle: (id: string, checked: boolean) => void;
  /** When true, `options` are already filtered server-side, so no client-side query filter. */
  serverSearch?: boolean;
  /** Called with the typed query so a parent can drive server-side search. */
  onQueryChange?: (query: string) => void;
  /** Resolves selected-chip names for ids not present in the current `options`. */
  nameById?: Map<string, string>;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const byId = useMemo(() => new Map(options.map((o) => [o.id, o.name])), [options]);
  const resolveName = (id: string): string => byId.get(id) ?? nameById?.get(id) ?? 'Unknown';
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) =>
        !selected.includes(o.id) && (serverSearch || q === '' || o.name.toLowerCase().includes(q)),
    );
  }, [options, selected, query, serverSearch]);

  return (
    <div ref={containerRef}>
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1 rounded-lg border border-slate-200 p-2">
        {selected.length > 0 ? (
          <div className="mb-2 flex flex-wrap gap-1">
            {selected.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700"
              >
                {resolveName(id)}
                <button
                  type="button"
                  className="text-brand-500 hover:text-brand-800"
                  aria-label={`Remove ${resolveName(id)}`}
                  onClick={() => onToggle(id, false)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}
        {!serverSearch && options.length === 0 ? (
          <p className="px-1 py-0.5 text-xs text-slate-400">{emptyText}</p>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={query}
              placeholder={placeholder}
              className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
                onQueryChange?.(e.target.value);
              }}
            />
            {open ? (
              <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                {matches.length === 0 ? (
                  <li className="px-3 py-1.5 text-xs text-slate-400">
                    {serverSearch && query.trim() === '' ? 'Type to search…' : 'No matches.'}
                  </li>
                ) : (
                  matches.map((option) => (
                    <li key={option.id}>
                      <button
                        type="button"
                        className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                        onClick={() => {
                          onToggle(option.id, true);
                          setQuery('');
                          onQueryChange?.('');
                        }}
                      >
                        {option.name}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

const TABS: ModalTab[] = [
  { id: 'details', label: 'Details' },
  { id: 'products', label: 'Products' },
  { id: 'quotes', label: 'Quotes' },
  { id: 'cpq', label: 'CPQ' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'notes', label: 'Notes' },
  { id: 'files', label: 'Files' },
  { id: 'history', label: 'History' },
];

const FORM_ID = 'opportunity-form';

export function OpportunityFormModal({
  isOpen,
  opportunity: opportunityProp,
  initialCustomerId,
  onClose,
}: OpportunityFormModalProps): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnOnboardingId = searchParams.get('returnOnboardingId');
  // Local copy so a "Save" (keep open) on create can switch the form into edit
  // mode of the just-created record without waiting for the parent to re-render.
  const [opportunity, setOpportunity] = useState<Opportunity | null>(opportunityProp);
  const isEdit = opportunity !== null;
  const { isAdmin, can } = useAuth();
  const canEdit = can('sales', 'edit');
  const saveMode = useSaveMode();

  const [form, setForm] = useState<FormState>(() => toFormState(opportunity));
  const [activeTab, setActiveTab] = useState('details');
  // The CPQ integration is temporary: when an admin switches it off on the ConnectWise CPQ
  // integration page, its tab disappears here too.
  const cpqStatus = useCpqStatus();
  const cpqEnabled = cpqStatus.data?.enabled ?? true;
  const tabs = useMemo(() => (cpqEnabled ? TABS : TABS.filter((t) => t.id !== 'cpq')), [cpqEnabled]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [customerCreateOpen, setCustomerCreateOpen] = useState(false);
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [netSuiteContactCreateOpen, setNetSuiteContactCreateOpen] = useState(false);

  const createMutation = useCreateOpportunity();
  const updateMutation = useUpdateOpportunity();
  const deleteMutation = useDeleteOpportunity();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetDelete = deleteMutation.reset;

  const membersQuery = useMemberOptions();
  const categoriesQuery = useServiceCategoryOptions();
  const contactsQuery = useContacts({ customerId: form.customerId || undefined, pageSize: 200 });

  // NetSuite Team sources from contacts of the company literally named "NetSuite".
  const netSuiteCustomerQuery = useCustomers({ page: 1, pageSize: 20, search: 'NetSuite' });
  const netSuiteCustomerId = useMemo(
    () =>
      (netSuiteCustomerQuery.data?.data ?? []).find(
        (c) => c.name.trim().toLowerCase() === 'netsuite',
      )?.id,
    [netSuiteCustomerQuery.data],
  );
  // Server-side search so the picker finds any NetSuite contact, not just the first 200.
  const [netSuiteSearch, setNetSuiteSearch] = useState('');
  const [netSuiteSearchDebounced, setNetSuiteSearchDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setNetSuiteSearchDebounced(netSuiteSearch.trim()), 250);
    return () => clearTimeout(timer);
  }, [netSuiteSearch]);
  const netSuiteContactsQuery = useContacts({
    customerId: netSuiteCustomerId,
    pageSize: 200,
    search: netSuiteSearchDebounced || undefined,
  });
  const netSuiteTeamOptions = useMemo(
    () =>
      (netSuiteContactsQuery.data?.data ?? []).map((contact) => ({
        id: contact.id,
        name: `${contact.firstName} ${contact.lastName ?? ''}`.trim(),
      })),
    [netSuiteContactsQuery.data],
  );
  // Accumulate id→name across searches so selected chips keep their names once seen.
  const netSuiteNameById = useRef(new Map<string, string>());
  useEffect(() => {
    for (const option of netSuiteTeamOptions) netSuiteNameById.current.set(option.id, option.name);
  }, [netSuiteTeamOptions]);

  const categoryGridOptions = useMemo(
    () => (categoriesQuery.data ?? []).map((c) => ({ id: c.id, name: c.name })),
    [categoriesQuery.data],
  );

  const toggleId =
    (key: 'netSuiteTeamContactIds' | 'currentProductCategoryIds' | 'currentServiceCategoryIds') =>
    (id: string, checked: boolean): void =>
      setForm((prev) => ({
        ...prev,
        [key]: checked ? [...prev[key], id] : prev[key].filter((existing) => existing !== id),
      }));

  const memberOptions = useMemo(
    () => [
      { value: '', label: 'Unassigned' },
      ...(membersQuery.data ?? [])
        // Only sales reps, but keep the already-selected member so edits never lose the value.
        .filter((member) => member.isSalesRep || member.id === form.salesRepId)
        .map((member) => ({
          value: member.id,
          label: member.displayName,
        })),
    ],
    [membersQuery.data, form.salesRepId],
  );

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'None' },
      ...(categoriesQuery.data ?? []).map((category) => ({
        value: category.id,
        label: category.name,
      })),
    ],
    [categoriesQuery.data],
  );

  const contactOptions = useMemo(
    () => [
      { value: '', label: 'None' },
      ...(contactsQuery.data?.data ?? []).map((contact) => ({
        value: contact.id,
        label: `${contact.firstName} ${contact.lastName ?? ''}`.trim(),
      })),
    ],
    [contactsQuery.data],
  );

  const resetSaveMode = saveMode.reset;

  // Sync the incoming prop into local state whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) {
      setOpportunity(opportunityProp);
    }
  }, [isOpen, opportunityProp]);

  // Reset the form whenever the active record changes (on open, or after a
  // create-and-keep-open switches the form into edit mode of the new record).
  useEffect(() => {
    if (isOpen) {
      setForm(
        opportunity
          ? toFormState(opportunity)
          : { ...EMPTY_FORM, customerId: initialCustomerId ?? '' },
      );
      setActiveTab('details');
      setConfirmOpen(false);
      setClientErrors({});
      setCustomerCreateOpen(false);
      setContactCreateOpen(false);
      setNetSuiteContactCreateOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
      resetDelete();
    }
  }, [isOpen, opportunity, initialCustomerId, resetSaveMode, resetCreate, resetUpdate, resetDelete]);

  // The CPQ status loads asynchronously, so fall back to Details if the tab disappears
  // while it is open.
  useEffect(() => {
    if (!cpqEnabled && activeTab === 'cpq') setActiveTab('details');
  }, [cpqEnabled, activeTab]);

  const serverFieldErrors = fieldErrorsOf(mutation.error);
  const fieldErrors = { ...serverFieldErrors, ...clientErrors };
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save opportunity.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    // These fields are mandatory on the opportunity form.
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Name is required.';
    if (!isEdit && !form.customerId) errors.customerId = 'Company is required.';
    if (!form.contactId) errors.contactId = 'Contact is required.';
    if (!form.salesRepId) errors.salesRepId = 'Sales rep is required.';
    if (!form.serviceCategoryId) errors.serviceCategoryId = 'Type is required.';
    if (!form.closeDate) errors.closeDate = 'Close date is required.';
    if (Object.keys(errors).length > 0) {
      setClientErrors(errors);
      setActiveTab('details');
      return;
    }
    setClientErrors({});
    const shared = {
      name: form.name.trim(),
      contactId: form.contactId ? form.contactId : null,
      salesRepId: form.salesRepId ? form.salesRepId : null,
      serviceCategoryId: form.serviceCategoryId ? form.serviceCategoryId : null,
      closeDate: form.closeDate ? form.closeDate : null,
      stage: form.stage,
      status: form.status,
      kind: form.kind ? form.kind : null,
      netSuiteTeamContactIds: form.netSuiteTeamContactIds,
      lastReviewedDate: form.lastReviewedDate ? form.lastReviewedDate : null,
      summary: form.summary.trim() ? form.summary.trim() : null,
      currentProductCategoryIds: form.currentProductCategoryIds,
      currentServiceCategoryIds: form.currentServiceCategoryIds,
      nsReg: form.nsReg.trim() ? form.nsReg.trim() : null,
      avalaraReg: form.avalaraReg.trim() ? form.avalaraReg.trim() : null,
      lengthOfProject:
        form.lengthOfProject.trim() === '' || !Number.isFinite(Number(form.lengthOfProject))
          ? null
          : Math.trunc(Number(form.lengthOfProject)),
      lengthOfAgreement:
        form.lengthOfAgreement.trim() === '' || !Number.isFinite(Number(form.lengthOfAgreement))
          ? null
          : Math.trunc(Number(form.lengthOfAgreement)),
      marketingAcquisition: form.marketingAcquisition,
    };

    const closeAfterSave = saveMode.closeAfterSaveRef.current;
    if (isEdit) {
      updateMutation.mutate(
        { id: opportunity.id, input: shared },
        { onSuccess: () => closeAfterSave && onClose() },
      );
    } else {
      const payload: CreateOpportunityInput = { customerId: form.customerId, ...shared };
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (closeAfterSave) {
            onClose();
          } else {
            // Keep the form open in edit mode of the newly-created record.
            setOpportunity(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!opportunity) {
      return;
    }
    deleteMutation.mutate(opportunity.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  const goToCustomer = (): void => {
    const targetCustomerId = form.customerId || opportunity?.customerId;
    if (!targetCustomerId) {
      return;
    }
    navigate(`/customers?returnCustomerId=${targetCustomerId}&returnTab=opportunities`);
  };
  const footer =
    activeTab === 'details' ? (
      <FormActions
        formId={FORM_ID}
        saveMode={saveMode}
        isPending={mutation.isPending}
        onCancel={onClose}
        leading={
          isEdit && isAdmin ? (
            <Button
              variant="ghost"
              className="mr-auto text-rose-600 hover:bg-rose-50"
              onClick={() => setConfirmOpen(true)}
              disabled={mutation.isPending || deleteMutation.isPending}
            >
              Delete
            </Button>
          ) : null
        }
      />
    ) : (
      <>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </>
    );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? form.name || 'Edit opportunity' : 'New opportunity'}
        size="wide"
        headerActions={
          <div className="flex items-center gap-2">
            {returnOnboardingId ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/sales/onboarding/${returnOnboardingId}`)}
              >
                ← Back to onboarding
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              onClick={goToCustomer}
              disabled={!form.customerId && !opportunity?.customerId}
            >
              Go to Customer
            </Button>
          </div>
        }
        tabs={tabs}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        footer={footer}
      >
        {activeTab === 'details' ? (
          <form id={FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-4">
            {generalError ? (
              <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {generalError}
              </p>
            ) : null}

            {isEdit ? (
              <div className="rounded-lg bg-slate-50 px-3 py-2">
                <span className="text-xs uppercase tracking-wide text-slate-500">Company</span>
                <p className="text-sm font-semibold text-slate-900">{opportunity.customerName}</p>
              </div>
            ) : null}

            <TextField
              label="Name"
              required
              value={form.name}
              error={fieldErrors.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />

            {isEdit ? null : (
              <div>
                <CustomerSelectField
                  label="Company"
                  required
                  value={form.customerId}
                  placeholder="Select a company…"
                  error={fieldErrors.customerId}
                  onChange={(customerId) =>
                    setForm((prev) => ({ ...prev, customerId, contactId: '' }))
                  }
                />
                {canEdit ? (
                  <button
                    type="button"
                    className="mt-1 text-xs font-medium text-brand-600 hover:text-brand-800"
                    onClick={() => setCustomerCreateOpen(true)}
                  >
                    + New company
                  </button>
                ) : null}
              </div>
            )}

            <div>
              <SelectField
                label="Contact"
                required
                value={form.contactId}
                options={contactOptions}
                error={fieldErrors.contactId}
                disabled={!form.customerId}
                onChange={(e) => setForm((prev) => ({ ...prev, contactId: e.target.value }))}
              />
              {canEdit && form.customerId ? (
                <button
                  type="button"
                  className="mt-1 text-xs font-medium text-brand-600 hover:text-brand-800"
                  onClick={() => setContactCreateOpen(true)}
                >
                  + New contact
                </button>
              ) : null}
            </div>

            <SelectField
              label="Sales rep"
              required
              value={form.salesRepId}
              options={memberOptions}
              error={fieldErrors.salesRepId}
              onChange={(e) => setForm((prev) => ({ ...prev, salesRepId: e.target.value }))}
            />

            <SelectField
              label="Type"
              required
              value={form.serviceCategoryId}
              options={categoryOptions}
              error={fieldErrors.serviceCategoryId}
              onChange={(e) => setForm((prev) => ({ ...prev, serviceCategoryId: e.target.value }))}
            />

            <TextField
              label="Close date"
              type="date"
              required
              value={form.closeDate}
              error={fieldErrors.closeDate}
              onChange={(e) => setForm((prev) => ({ ...prev, closeDate: e.target.value }))}
            />

            <div className="grid grid-cols-2 gap-4">
              <SelectField
                label="Stage"
                value={form.stage}
                options={STAGE_OPTIONS}
                error={fieldErrors.stage}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, stage: e.target.value as OpportunityStage }))
                }
              />
              <SelectField
                label="Status"
                value={form.status}
                options={STATUS_OPTIONS}
                error={fieldErrors.status}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, status: e.target.value as OpportunityStatus }))
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <SelectField
                label="New or Upsell"
                value={form.kind}
                options={KIND_OPTIONS}
                error={fieldErrors.kind}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, kind: e.target.value as '' | OpportunityKind }))
                }
              />
              <TextField
                label="Last reviewed date"
                type="date"
                value={form.lastReviewedDate}
                error={fieldErrors.lastReviewedDate}
                onChange={(e) => setForm((prev) => ({ ...prev, lastReviewedDate: e.target.value }))}
              />
            </div>

            <TextAreaField
              label="Summary"
              rows={2}
              value={form.summary}
              error={fieldErrors.summary}
              onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
            />

            <MultiCheckGrid
              label="Current Products"
              options={categoryGridOptions}
              selected={form.currentProductCategoryIds}
              emptyText="No service categories configured."
              onToggle={toggleId('currentProductCategoryIds')}
            />

            <MultiCheckGrid
              label="Current Services"
              options={categoryGridOptions}
              selected={form.currentServiceCategoryIds}
              emptyText="No service categories configured."
              onToggle={toggleId('currentServiceCategoryIds')}
            />

            <MultiSelectSearch
              label="NetSuite Team"
              options={netSuiteTeamOptions}
              selected={form.netSuiteTeamContactIds}
              placeholder="Search NetSuite contacts…"
              serverSearch
              onQueryChange={setNetSuiteSearch}
              nameById={netSuiteNameById.current}
              emptyText={
                netSuiteCustomerId
                  ? 'The “NetSuite” company has no contacts.'
                  : 'No company named “NetSuite” was found.'
              }
              onToggle={toggleId('netSuiteTeamContactIds')}
            />
            {canEdit && netSuiteCustomerId ? (
              <button
                type="button"
                className="-mt-2 self-start text-xs font-medium text-brand-600 hover:text-brand-800"
                onClick={() => setNetSuiteContactCreateOpen(true)}
              >
                + New NetSuite contact
              </button>
            ) : null}

            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="NS Reg"
                value={form.nsReg}
                maxLength={256}
                error={fieldErrors.nsReg}
                onChange={(e) => setForm((prev) => ({ ...prev, nsReg: e.target.value }))}
              />
              <TextField
                label="Avalara Reg"
                value={form.avalaraReg}
                maxLength={256}
                error={fieldErrors.avalaraReg}
                onChange={(e) => setForm((prev) => ({ ...prev, avalaraReg: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="Length of Project"
                type="number"
                value={form.lengthOfProject}
                error={fieldErrors.lengthOfProject}
                onChange={(e) => setForm((prev) => ({ ...prev, lengthOfProject: e.target.value }))}
              />
              <TextField
                label="Length of Agreement"
                type="number"
                value={form.lengthOfAgreement}
                error={fieldErrors.lengthOfAgreement}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, lengthOfAgreement: e.target.value }))
                }
              />
            </div>

            <CheckboxField
              label="Marketing Acquisition"
              checked={form.marketingAcquisition}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, marketingAcquisition: e.target.checked }))
              }
            />
          </form>
        ) : null}

        {activeTab === 'products' ? (
          isEdit ? (
            <OpportunityProductsSection
              opportunityId={opportunity.id}
              opportunityServiceCategoryId={opportunity.serviceCategoryId}
              canEdit={canEdit}
            />
          ) : (
            <p className="text-sm text-slate-500">
              Create the opportunity first, then add products.
            </p>
          )
        ) : null}

        {activeTab === 'quotes' ? (
          isEdit ? (
            <OpportunityQuotesSection opportunityId={opportunity.id} canEdit={canEdit} />
          ) : (
            <p className="text-sm text-slate-500">
              Create the opportunity first, then create a quote.
            </p>
          )
        ) : null}

        {activeTab === 'cpq' && cpqEnabled ? (
          isEdit ? (
            <OpportunityCpqQuoteSection opportunityId={opportunity.id} canEdit={canEdit} />
          ) : (
            <p className="text-sm text-slate-500">
              Create the opportunity first, then link a CPQ quote.
            </p>
          )
        ) : null}

        {activeTab === 'onboarding' ? (
          isEdit ? (
            <OnboardingRecordsTab opportunityId={opportunity.id} />
          ) : (
            <p className="text-sm text-slate-500">
              An onboarding record is created once the opportunity is set to Order Approved.
            </p>
          )
        ) : null}

        {activeTab === 'notes' ? (
          isEdit ? (
            <OpportunityNotesSection opportunityId={opportunity.id} canEdit={canEdit} />
          ) : (
            <p className="text-sm text-slate-500">Create the opportunity first, then add notes.</p>
          )
        ) : null}

        {activeTab === 'files' ? (
          isEdit ? (
            <OpportunityAttachmentsSection opportunityId={opportunity.id} canEdit={canEdit} />
          ) : (
            <p className="text-sm text-slate-500">
              Create the opportunity first, then attach files.
            </p>
          )
        ) : null}

        {activeTab === 'history' ? (
          isEdit ? (
            <HistoryPanel entityType="Opportunity" entityId={opportunity.id} />
          ) : (
            <p className="text-sm text-slate-500">
              Create the opportunity first to see its history.
            </p>
          )
        ) : null}
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete opportunity"
        message="This permanently deletes the opportunity along with its products and notes."
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      <QuickCreateCustomerModal
        isOpen={customerCreateOpen}
        onClose={() => setCustomerCreateOpen(false)}
        onCreated={(customer: Customer) => {
          setForm((prev) => ({ ...prev, customerId: customer.id, contactId: '' }));
          setClientErrors((prev) => {
            const { customerId: _removed, ...rest } = prev;
            return rest;
          });
        }}
      />

      <QuickCreateContactModal
        isOpen={contactCreateOpen}
        customerId={form.customerId}
        onClose={() => setContactCreateOpen(false)}
        onCreated={(contact: Contact) => {
          setForm((prev) => ({ ...prev, contactId: contact.id }));
          setClientErrors((prev) => {
            const { contactId: _removed, ...rest } = prev;
            return rest;
          });
        }}
      />

      <QuickCreateContactModal
        isOpen={netSuiteContactCreateOpen}
        customerId={netSuiteCustomerId ?? ''}
        title="New NetSuite contact"
        onClose={() => setNetSuiteContactCreateOpen(false)}
        onCreated={(contact: Contact) => {
          netSuiteNameById.current.set(
            contact.id,
            `${contact.firstName} ${contact.lastName ?? ''}`.trim(),
          );
          setForm((prev) => ({
            ...prev,
            netSuiteTeamContactIds: prev.netSuiteTeamContactIds.includes(contact.id)
              ? prev.netSuiteTeamContactIds
              : [...prev.netSuiteTeamContactIds, contact.id],
          }));
        }}
      />
    </>
  );
}
